import { useEffect, useLayoutEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { authenticateTelegram } from './auth-client.js';
import { ApiClientError, subscribeSessionExpired } from './api.js';
import { getTelegram } from './telegram.js';
import { LocalAcceptanceLogin, localAcceptanceEnabled } from './local-acceptance.js';
import {
  clearLocalAcceptanceSession,
  restoreLocalAcceptanceSession,
} from './local-acceptance-session.js';
import { useSession } from './session.js';
import { authenticatedRouteReplacement } from './session-route.js';
import { AsyncState, Button, Screen } from './ui.js';
import { DeletionCompletion } from './deletion-completion.js';
import { LegalStatusBoundary } from './legal-consent.js';

export function SessionGate() {
  const queryClient = useQueryClient();
  const ready = useSession((state) => state.ready);
  const user = useSession((state) => state.user);
  const deletionPhase = useSession((state) => state.deletionPhase);
  const setSession = useSession((state) => state.setSession);
  const expireSession = useSession((state) => state.expire);
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<ApiClientError | null>(null);
  const telegram = getTelegram();
  const initData =
    telegram?.initData || (import.meta.env.DEV ? import.meta.env.VITE_LOCAL_INIT_DATA : '');
  const [restoredLocalSession, setRestoredLocalSession] = useState(() =>
    !initData && localAcceptanceEnabled() ? restoreLocalAcceptanceSession() : null,
  );

  useLayoutEffect(
    () =>
      subscribeSessionExpired(() => {
        if (useSession.getState().deletionPhase !== 'NONE') return;
        queryClient.clear();
        clearLocalAcceptanceSession();
        setRestoredLocalSession(null);
        setError(null);
        expireSession();
      }),
    [expireSession, queryClient],
  );

  useEffect(() => {
    if (ready || !initData || deletionPhase !== 'NONE') return;
    void authenticateTelegram(initData)
      .then((result) => {
        if (useSession.getState().deletionPhase !== 'NONE') return;
        setSession(result.user);
        const replacement = authenticatedRouteReplacement(location.pathname, result.nextRoute);
        if (replacement) void navigate(replacement, { replace: true });
      })
      .catch((reason: unknown) => {
        setError(
          reason instanceof ApiClientError
            ? reason
            : new ApiClientError('NETWORK_ERROR', 'Нет соединения'),
        );
      });
  }, [initData, location.pathname, navigate, ready, setSession, deletionPhase]);

  useEffect(() => {
    if (!ready && restoredLocalSession) setSession(restoredLocalSession.user);
  }, [ready, restoredLocalSession, setSession]);

  if (deletionPhase === 'ACCEPTED') return <DeletionCompletion />;
  if (deletionPhase === 'FAILED')
    return (
      <Screen title="Не удалось подтвердить удаление">
        <p role="alert">Откройте приложение заново, чтобы проверить состояние аккаунта.</p>
        <Button onClick={() => telegram?.close()}>Закрыть приложение</Button>
      </Screen>
    );
  if (deletionPhase === 'REQUESTING')
    return (
      <Screen title="Удаляем аккаунт">
        <AsyncState kind="loading" />
      </Screen>
    );
  if (ready && user) {
    const management =
      location.pathname.startsWith('/legal/') ||
      location.pathname === '/support' ||
      location.pathname.startsWith('/settings/account') ||
      location.pathname === '/settings/data' ||
      location.pathname === '/account/status' ||
      location.pathname === '/welcome';
    return management ? (
      <Outlet />
    ) : (
      <LegalStatusBoundary>
        <Outlet />
      </LegalStatusBoundary>
    );
  }
  if (restoredLocalSession)
    return (
      <Screen title="Восстанавливаем local acceptance">
        <AsyncState kind="loading" />
      </Screen>
    );
  if (!initData && localAcceptanceEnabled())
    return (
      <LocalAcceptanceLogin
        onAuthenticated={(result) => {
          setSession(result.user);
          const replacement = authenticatedRouteReplacement(location.pathname, result.nextRoute);
          if (replacement) void navigate(replacement, { replace: true });
        }}
      />
    );
  if (!initData)
    return (
      <Screen title="Откройте PONCHIK в Telegram" eyebrow="Безопасный вход">
        <p className="lead">Авторизация работает только через официальный Telegram Mini App.</p>
      </Screen>
    );
  if (error)
    return (
      <Screen title="Нужно переоткрыть PONCHIK" eyebrow="Сессия не восстановлена">
        <AsyncState kind="error" {...(error.requestId ? { requestId: error.requestId } : {})} />
        <p className="hint">Закройте Mini App и снова откройте его кнопкой в чате с ботом.</p>
        <Button onClick={() => telegram?.close()}>Закрыть Mini App</Button>
      </Screen>
    );
  return (
    <Screen title="Входим через Telegram" eyebrow="Безопасный вход">
      <AsyncState kind="loading" />
    </Screen>
  );
}
